import { jsx } from 'react/jsx-runtime';
import { Component, useContext, createContext, useEffect } from 'react';

const BRAND_TYPE_BY_ID = {
    imn: "b2c",
    iwb: "b2c",
    iwt: "b2c",
    iwtat: "b2c",
    li: "b2c",
    sl: "b2c",
    sln: "b2c",
    slp: "b2b"
};

const STAGES = [
    "dev",
    "preview",
    "live"
];
const ENVIRONMENTS = [
    "local",
    ...STAGES
];
/**
 * Variable checking the server environment runtime.
 */ const IS_SERVER_ENVIRONMENT = typeof window === "undefined";
/**
 * Variable to detect the test environment.
 * Testing runtimes (Jest and Vitest) automatically set the `NODE_ENV` environment variable to `test`.
 * Thanks to the `DefinePlugin` setup, the bundler minifier automatically strips the conditional code statement if NODE_ENV !== "test".
 * @see {@link https://vitest.dev/guide/migration.html#envs Node environment injection}.
 */ const IS_TEST_ENVIRONMENT = process.env.NODE_ENV === "test";

const LANGUAGE_TAGS_VALUES = [
    "de-DE",
    "de-AT",
    "en-GB",
    "fr-BE",
    "fr-FR",
    "nl-BE"
];
const LANGUAGE_TAGS = new Set(LANGUAGE_TAGS_VALUES);
const LANGUAGES = new Set(LANGUAGE_TAGS_VALUES.map((tag)=>tag.split("-")[0]));
const REGIONS = new Set(LANGUAGE_TAGS_VALUES.map((tag)=>tag.split("-")[1]));

/**
 * Create a shell-like id.
 * @param component Optional component name owned by the shell.
 * @returns The shell-like id.
 */ const createShellLikeId = (component)=>{
    return `shell_ufrn${!component ? "" : `_${component}`}`;
};

/**
 * Follows the registry `CreateId` convention (app replaced with shell).
 * @see {@link https://github.com/axel-springer-kugawana/aviv_platform_unified_frontend_registry/blob/e6d26f7ec237210f8f928fd6c866f24a80b8a678/libraries/registry/types/index.ts#L12C13-L12C29 Id format}.
 */ const SHELL_ID = createShellLikeId();

/**
 * Check whether the provided id belongs to an application component.
 * @param id The id to test from.
 * @returns true if the provided value is a application-like id, false otherwise.
 */ const isApplicationLikeId = (id)=>{
    return id.startsWith("app_");
};

/**
 * Check whether the provided id belongs to a micro-frontend component.
 * @param id The id to test from.
 * @returns true if the provided value is a micro-frontend-like id, false otherwise.
 */ const isMicroFrontendLikeId = (id)=>{
    return id.startsWith("mfe_");
};

/**
 * Check whether the provided id belongs to an application or micro-frontend component.
 * @param id The id to test from.
 * @returns true if the provided value is a remote-like id, false otherwise.
 */ const isRemoteLikeId = (id)=>{
    return isApplicationLikeId(id) || isMicroFrontendLikeId(id);
};

/**
 * Check whether the provided id belongs to a shell component.
 * @param id The id to test from.
 * @returns true if the provided value is a shell-like id, false otherwise.
 */ const isShellLikeId = (id)=>{
    return id.startsWith("shell_");
};

/**
 * ErrorBoundary component to catch errors in children components.
 * @see {@link https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary ErrorBoundary}
 */ class ErrorBoundary extends Component {
    constructor(props){
        super(props);
        this.state = {
            hasError: false
        };
    }
    handleError(...input) {
        const log = this.props.onError ?? console.error;
        log(...input);
    }
    static getDerivedStateFromError() {
        return {
            hasError: true
        };
    }
    componentDidCatch(error, errorInfo) {
        this.handleError(error, {
            id: this.props.id,
            errorInfo
        });
    }
    render() {
        const { fallback: Fallback, children } = this.props;
        const { hasError } = this.state;
        if (hasError && Fallback) {
            return /*#__PURE__*/ jsx(Fallback, {});
        }
        /**
     * The ErrorBoundary component only catches client-side errors and does not handle errors during server-side rendering (SSR) with `renderToString`.
     * To mitigate this, since React v18 and following the [server errors RFC](https://github.com/reactjs/rfcs/blob/ba9bd5744cb922184ec9390515910cd104a30c6e/text/0215-server-errors-in-react-18.md),
     * to recover from errors thrown on the server side, the React tree is wrapped with a suspense to display, by default, an error fallback in case of unmanaged rendering error or unhandled lazy behaviors consumer side.
     * When an error is thrown on the server, React will emit the fallback HTML from the server and then automatically retry rendering on the client.
     *
     * Although this approach works, it causes hydration errors
     * (e.g., "Uncaught Error: This Suspense boundary received an update before it finished hydrating").
     * @see {@link https://github.com/reactjs/rfcs/blob/ba9bd5744cb922184ec9390515910cd104a30c6e/text/0215-server-errors-in-react-18.md Server errors RFC}
     * @see {@link https://react.dev/reference/react-dom/server/renderToString#caveats renderToString caveats}
     */ return children;
    }
}

/**
 * A factory to format an error message in a consistent way.
 * @param configuration The configuration object.
 * @returns The formatted error message.
 */ const createErrorMessage = ({ cause, solution })=>{
    return `❌ An error occurred: ${cause}.\n✅ To solve the issue: ${solution}.`;
};

/**
 * A factory to uniformize the way global module variables are named.
 * It prefixes each module by the Unified Frontend {@link https://github.com/axel-springer-kugawana/aviv_platform_unified_frontend_registry/blob/6d544d0fb00088c7af670924bb25150128a45285/libraries/registry/types/index.ts#L139 capability code}.
 * @param module - The module name.
 * @param component - Optional component name to append.
 * @returns The global key name derivated from the input (used to scope global states linked to a module).
 */ const createGlobalKey = (module, component)=>{
    return `__UFRN_${module.toUpperCase()}${!component ? "" : `_${component.toUpperCase()}`}__`;
};

/**
 * List of remotes that are allowed to be loaded without build package & lifecycle
 *
 * The goal is to gradually reduce the list of remotes allowed to load without build package and lifecycle until no remotes remain whitelisted.
 * This ensures all remotes adhere to the shell's standards for packaging and lifecycle, improving overall consistency, security, and maintainability of the architecture.
 * The shell team should regularly review the current list to identify and migrate whitelisted remotes to a compliant integration.
 *
 */ const ALLOWED_REMOTES = [
    "app_owpp_houseValuationLandingPage",
    "app_owpp_houseValueLandingPage",
    "app_owpp_marketValueLandingPage",
    "app_owpp_realEstateValueLandingPage",
    "app_owpp_sellHouseLandingPage",
    "app_seom",
    // micro frontends
    "mfe_avis_agencyInTheSpotlight",
    "mfe_avis_pricePagesAgencyCards"
];
const ALLOWED_WITHOUT_BUILD_PACKAGE = [
    ...ALLOWED_REMOTES
];
const ALLOWED_WITHOUT_LIFECYCLE = [
    ...ALLOWED_REMOTES,
    "app_cmsg_inbox",
    "app_conp_favoritesOffline",
    "app_iprf",
    "app_owcl_contact",
    "app_owcl_myListings",
    "app_owpp_nutzungsdauerLandingPage",
    "app_skpf_seeker"
];

const GLOBAL_KEY = createGlobalKey("BUILD_FINGERPRINTS");
const createBuildFingerprints = (id)=>`
  if (!window["${GLOBAL_KEY}"]) {
    window["${GLOBAL_KEY}"] = new Set();
  }
 window["${GLOBAL_KEY}"].add("${id}");
`;
const assertBuildModuleUsage = (id)=>{
    const isUsingBuildModule = window[GLOBAL_KEY]?.has(id);
    if (!ALLOWED_WITHOUT_BUILD_PACKAGE.includes(id) && !isUsingBuildModule) {
        throw new Error(createErrorMessage({
            cause: `The component \`${id}\` is not allowed to be built without the \`@aviv-unified-frontend/build\` package`,
            solution: "Make sure the component uses the `@aviv-unified-frontend/build` package with version >= 1.7.1"
        }));
    }
    if (!isUsingBuildModule) {
        console.warn(`The application \`${id}\` is not using the latest \`@aviv-unified-frontend/build\` package. Make sure the application uses the latest \`@aviv-unified-frontend/build\` package.`);
    }
};
const assertLifecycleModuleUsage = (id, isCreateApplication)=>{
    if (!isCreateApplication && !ALLOWED_WITHOUT_LIFECYCLE.includes(id)) {
        throw new Error(createErrorMessage({
            cause: `The application \`${id}\` is not allowed to be loaded as a non-\`createApplication\` instance`,
            solution: `Make sure to default export a \`createApplication\` instance in the application entry point`
        }));
    }
    if (!isCreateApplication) {
        console.warn(`The application \`${id}\` is not a \`createApplication\` instance. Make sure to default export a \`createApplication\` instance in the application entry point.`);
    }
};
const assertShellRequirements = (id, isCreateApplication)=>{
    try {
        assertBuildModuleUsage(id);
        assertLifecycleModuleUsage(id, isCreateApplication);
    } catch (error) {
        /**
     * Logging the error here ensures that even if the error is rethrown and caught upstream,
     * we have a trace of it at this specific context. This is particularly useful for
     * debugging recoverable React errors (e.g., hydration issues) that might not always
     * preserve the original error in the `Error.cause` field.
     *
     * @see {@link https://github.com/axel-springer-kugawana/aviv_platform_unified_frontend/pull/1075#discussion_r1869837731 }
     */ console.error(error);
        throw error;
    }
};

const IsIntantiatedContext = /*#__PURE__*/ createContext(false);
/**
 * A higher-order-factory-like helper to enforce a single component instance in the tree.
 * @param componentName The component name.
 * @param Component The targetted React component to decorate with the singleton behavior (most of the time, Provider-like component).
 * @returns The decorated component with singleton behavior.
 */ const createSingletonComponent = (componentName, Component)=>{
    return (props)=>{
        const isInstantiated = useContext(IsIntantiatedContext);
        if (isInstantiated) {
            console.error(createErrorMessage({
                cause: `The \`${componentName}\` component cannot be instantiated more than once`,
                solution: `The singleton behavior has been automatically enforced by skipping the nested \`${componentName}\` element creation. To remove the log and prevent any introduced side effect(s), please remove any \`${componentName}\` component instance in your component tree outside the sandbox entry point.\nIf the log still persists, make sure the sandbox entry point is not included in your production bundle`
            }));
            return props.children;
        }
        return /*#__PURE__*/ jsx(IsIntantiatedContext.Provider, {
            value: true,
            children: /*#__PURE__*/ jsx(Component, {
                ...props
            })
        });
    };
};

/**
 * A cache to store the warnings that have already been logged.
 * This is to prevent duplicate warnings from being logged.
 */ const WARNINGS_CACHE = new Set();
const decorateWithFieldDeprecation = (object, { /**
     * Whether to enable the deprecation warnings.
     * If set to `false`, the deprecation warnings will not be logged.
     *
     * This property ensures that warnings are not triggered before the release,
     * which would create confusion for our customers who would not be able to apply the changes since the new version would not yet be available.
     *
     */ enable, /**
     * Whether to define the deprecated fields.
     */ fields, /**
     * The module name where the deprecated fields are defined.
     */ module, /**
     * The package name where the deprecated fields are defined.
     */ packageName })=>{
    if (Object.keys(fields).length === 0) {
        throw new Error(createErrorMessage({
            cause: "No deprecated properties were provided",
            solution: "Please provide at least one deprecated property to warn about or remove the `decorateWithFieldDeprecation` call"
        }));
    }
    if (!enable) {
        return object;
    }
    const handler = {
        get (target, prop) {
            if (!IS_SERVER_ENVIRONMENT && fields[prop] && !WARNINGS_CACHE.has(prop)) {
                const { newField } = fields[prop];
                console.warn(`⚠️ DEPRECATION WARNING ⚠️\n\n
          The field \`${prop}\` from \`${module}\` is deprecated.\n
          Please update \`${packageName}\` & use \`${newField}\` instead.\n
          The field \`${prop}\` will be removed in the next major version.`);
                WARNINGS_CACHE.add(prop);
            }
            return target[prop];
        }
    };
    return new Proxy(object, handler);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getComponentName = (input)=>input.displayName || input.name || "Component";

/**
 * Get a state value stored globally depending on the runtime (server vs. client runtime).
 *
 * @param key The global store key.
 * @param fallbackValue A fallback value in case no store value is defined globally (used server side as a value source of truth).
 * @returns The found state value (always defined).
 */ const getGlobalStoreValue = (key, fallbackValue)=>{
    if (IS_SERVER_ENVIRONMENT) return fallbackValue;
    const states = Object.entries(fallbackValue);
    const hydrationSource = window[key];
    const output = {};
    for (const [stateKey, stateValue] of states){
        output[stateKey] = hydrationSource?.[stateKey] ?? stateValue;
    }
    return output;
};

const isRecord = (value)=>value !== null && typeof value === "object" && !Array.isArray(value);

const isValidSemanticVersion = (version)=>{
    /**
   * Regular expression to validate semver version
   * @see {@link https://semver.org/#is-there-a-suggested-regular-expression-regex-to-check-a-semver-string - Is there a suggested regular expression (RegEx) to check a SemVer string? }
   */ const semverRegex = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?(?:\+([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?$/;
    return semverRegex.test(version);
};

const parseVersion = (version)=>version.split(".").map((num)=>parseInt(num, 10) || 0);
const isMatchingVersion = ({ version, minVersion })=>{
    if (!isValidSemanticVersion(version)) {
        throw new Error(createErrorMessage({
            cause: "Invalid semver version is provided to `isMatchingVersion`",
            solution: "Please provide a valid semver version"
        }));
    }
    const [current, Min] = [
        parseVersion(version),
        parseVersion(minVersion)
    ];
    for(let i = 0; i < 3; i++){
        const version = current[i];
        const min = Min[i];
        if (version > min) return true;
        if (version < min) return false;
    }
    return true;
};

const isLocalEnvironment = (environment)=>{
    return environment === "local";
};
const isDevLikeEnvironment = (environment)=>{
    return isLocalEnvironment(environment) || environment === "dev";
};
/**
 * Determines the appropriate cookie domain based on the environment and configuration.
 *
 * This function handles cookie domain resolution for different deployment environments:
 * - For local environments, returns undefined to allow cookies to work on localhost
 * - For ephemeral environments (when versionEphemeral is provided), returns the ephemeral domain
 * - For standard environments (dev, preview, live), extracts and returns the domain from the origin URL
 *
 * @param origin - Object containing origin URLs for dev, preview, and live environments
 * @param environment - The current environment type (local, dev, preview, or live)
 * @param ephemeralDomain - The domain to use for ephemeral/temporary deployments
 * @param versionEphemeral - Version identifier for ephemeral deployments (empty string means not ephemeral)
 * @returns The cookie domain string, or undefined for local environments
 */ const getCookieDomain = (origin, environment, ephemeralDomain, versionEphemeral)=>{
    // If the environment is local, we return undefined to avoid setting domain for cookies,
    // as local environments typically run on localhost or similar, which doesn't require a domain.
    if (environment === "local") {
        return undefined;
    }
    if (versionEphemeral !== "") {
        return ephemeralDomain;
    }
    const url = new URL(origin[environment]);
    return url.hostname.replace("www.", "");
};

/**
 * Only use the Enhanced Module Federation plugin in dev & local environment to benefit from the additional features it provides.
 * https://module-federation.io/guide/basic/webpack.html
 */ const isRuntimeFederation = (environment)=>isDevLikeEnvironment(environment);

const extractLocale = (languageTag)=>{
    const [language, region] = languageTag.split("-");
    return {
        language,
        region
    };
};

/**
 * Serialize a Json object to string.
 *
 * It's a safer `JSON.stringify` alternative to prevent XSS vulnerabilities
 * during the HTML parsing phase by escaping all HTML-like tag syntax.
 *
 * @param input The JSON object.
 * @returns Safe stringified object representation.
 */ const serializeJson = (input)=>{
    return JSON.stringify(escapeHtml(JSON.stringify(input)));
};
/**
 * Escapes special characters and HTML entities in a given html string
 * Source of inspiration: https://github.com/vercel/next.js/blob/e2584c6fb3011f5150c28563b2c301eae5b290fd/packages/next/src/server/htmlescape.ts#L1
 * @param   content HTML string to escape for later insertion
 * @returns Escaped content
 */ const escapeHtml = (content)=>{
    return content.replace(ESCAPE_REGEX, (match)=>ESCAPE_LOOKUP[match]);
};
const ESCAPE_LOOKUP = {
    "&": "\\u0026",
    ">": "\\u003e",
    "<": "\\u003c",
    "\u2028": "\\u2028",
    "\u2029": "\\u2029"
};
const ESCAPE_REGEX = /[&><\u2028\u2029]/g;

/**
 * Serialize a record-like global store data to string.
 * @param key The global key.
 * @param value The record-like value.
 * @returns A serialized (string) script.
 */ const serializeGlobalStoreScript = (key, value)=>`<script>window["${key}"]=JSON.parse(${serializeJson(value)});</script>`;

/**
 * Error object has no enumerable properties by default, so information such as name, message are not strignify in JSON.
 * This function converts an error object to a plain object with enumerable properties.
 */ const deserializeErrors = (obj)=>Object.entries(obj).reduce((acc, [key, value])=>{
        const error = new Error();
        Object.keys(value).forEach((key)=>{
            if (value[key]) {
                error[key] = value[key];
            }
        });
        acc[key] = error;
        return acc;
    }, {});
const serializeErrors = (obj)=>Object.entries(obj).reduce((acc, [key, value])=>{
        const errorProperties = {};
        Object.getOwnPropertyNames(value).forEach((key)=>{
            errorProperties[key] = value[key];
        });
        // Error name is not enumerable
        errorProperties["name"] = value.name;
        acc[key] = errorProperties;
        return acc;
    }, {});

/**
 * A React hook to enforce the Provider definition before using a context.
 * @param scope Metadata describing the context scope (including package and module name).
 * @param context The React `Context` to check.
 * @returns The context value.
 */ const useSafeContext = (scope, context)=>{
    const value = useContext(context);
    if (value === null) {
        // Apply the fail-fast principle to prevent any issues in production (e.g. the ones with default values
        // extraneous the current deployment target (such as applying `AVIV_THEME` while the current brand is SeLoger))
        throw new Error(createErrorMessage({
            cause: `The \`${scope.module}\` module is not able to retrieve contextual data`,
            solution: `Verify your Shell integration by checking if the \`${scope.package}\` package is effectively shared.\nIf the issue is only reproducible in your local environment, make sure to simulate the Shell environment with the right component(s)`
        }));
    }
    return value;
};

function useThirdPartyScript({ src, onLoad }) {
    useEffect(()=>{
        if (!src) {
            throw new Error(createErrorMessage({
                cause: `The \`useThirdPartyScript\` hook requires a \`src\` parameter`,
                solution: `Make sure to provide a valid script URL to the \`src\` parameter`
            }));
        }
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
            return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        if (typeof onLoad === "function") {
            script.onload = ()=>{
                onLoad();
            };
        }
        document.body.appendChild(script);
    }, [
        src,
        onLoad
    ]);
}

export { BRAND_TYPE_BY_ID, ENVIRONMENTS, ErrorBoundary, IS_SERVER_ENVIRONMENT, IS_TEST_ENVIRONMENT, LANGUAGES, LANGUAGE_TAGS, REGIONS, SHELL_ID, assertShellRequirements, createBuildFingerprints, createErrorMessage, createGlobalKey, createShellLikeId, createSingletonComponent, decorateWithFieldDeprecation, deserializeErrors, extractLocale, getComponentName, getCookieDomain, getGlobalStoreValue, isApplicationLikeId, isDevLikeEnvironment, isLocalEnvironment, isMatchingVersion, isMicroFrontendLikeId, isRecord, isRemoteLikeId, isRuntimeFederation, isShellLikeId, serializeErrors, serializeGlobalStoreScript, serializeJson, useSafeContext, useThirdPartyScript };
